import React, { useEffect, useState } from 'react';
import useDebounce from '../../hooks/useDebounce';
import { fetchClients, fetchFilterCounts, Client, FilterCounts } from './services/clientsAPI';
import { useFilterStore } from '../../stores/filterStore';
import { useAuthStore } from '../../stores/authStore';
import ClientesPageSkeleton from './ClientesPageSkeleton';
import ClientDetailModal from './components/ClientDetailModal';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import { CarteiraSelector } from '../../components/CarteiraSelector/CarteiraSelector';
import CustomSelect from '../../components/CustomSelect';
import styles from './ClientesPage.module.css';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddIcon from '@mui/icons-material/Add';

const ORDENACAO_OPTIONS = [
    { value: 'ultima_compra', label: 'Última Compra' },
    { value: 'codigo', label: 'Código' },
    { value: 'nome', label: 'Nome' },
    { value: 'ultimos_cadastros', label: 'Últimos Cadastros' },
];

// Cache local para não piscar skeleton na troca de abas
let moduleCachedClients: Client[] = [];
let moduleCachedFilterCounts: FilterCounts | null = null;
let moduleCachedTotalItems: number = 0;
let moduleCachedTotalPages: number = 1;

const ClientesPage: React.FC = () => {
    const [clients, setClients] = useState<Client[]>(moduleCachedClients);
    const [loading, setLoading] = useState<boolean>(moduleCachedClients.length === 0);
    const [error, setError] = useState<string | null>(null);

    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const { selectedCarteiraId } = useFilterStore();
    const { role, user } = useAuthStore();
    const isSupervisor = role === 'supervisor' || role === 'diretor';
    // Ambiente AWS (cloud): não permite cadastrar novos clientes pelo dashboard
    const isAws = user?.isAws === true;

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(moduleCachedTotalPages);
    const [totalItems, setTotalItems] = useState<number>(moduleCachedTotalItems);
    const itemsPerPage = 30;

    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [selectedOrdenacao, setSelectedOrdenacao] = useState<string>('ultima_compra');
    const [selectedSegmento, setSelectedSegmento] = useState<string>('');
    const [selectedEstado, setSelectedEstado] = useState<string>('');
    const [selectedAtivo, setSelectedAtivo] = useState<string>('');

    const [filterCounts, setFilterCounts] = useState<FilterCounts>(moduleCachedFilterCounts || {
        segmentos: [],
        estados: [],
        cidades: [],
        status: [],
    });

    useEffect(() => {
        const loadFilterCounts = async () => {
            try {
                const response = await fetchFilterCounts(selectedCarteiraId || undefined);
                setFilterCounts(response.data);
                moduleCachedFilterCounts = response.data;
            } catch (err) {
                console.error('Erro ao carregar contadores:', err);
            }
        };
        loadFilterCounts();
    }, [selectedCarteiraId]);

    const loadClients = async () => {
        try {
            setLoading(true);
            const response = await fetchClients(
                currentPage, itemsPerPage,
                debouncedSearchTerm || undefined,
                selectedSegmento || undefined,
                selectedEstado || undefined,
                selectedAtivo || undefined,
                selectedCarteiraId || undefined,
                selectedOrdenacao || undefined,
            );
            setClients(response.data.clients);
            moduleCachedClients = response.data.clients;
            setTotalPages(response.data.pagination.totalPages);
            moduleCachedTotalPages = response.data.pagination.totalPages;
            setTotalItems(response.data.pagination.totalItems);
            moduleCachedTotalItems = response.data.pagination.totalItems;
            setError(null);
        } catch (err) {
            setError('Falha ao carregar dados da API.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadClients();
    }, [currentPage, debouncedSearchTerm, selectedSegmento, selectedEstado, selectedAtivo, selectedCarteiraId, selectedOrdenacao]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleNewClient = () => {
        setSelectedClientId(null);
        setIsModalOpen(true);
    };

    const handleClientClick = (id: number) => {
        setSelectedClientId(id);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedClientId(null);
    };

    const handleModalSuccess = () => {
        loadClients();
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedSegmento('');
        setSelectedEstado('');
        setSelectedAtivo('');
        setSelectedOrdenacao('ultima_compra');
        setCurrentPage(1);
    };

    const hasActiveFilters = searchTerm !== '' || selectedSegmento !== '' || selectedEstado !== '' || selectedAtivo !== '' || selectedOrdenacao !== 'ultima_compra';

    const formatCPFCNPJ = (documento: string) => {
        if (!documento) return '';
        const cleaned = documento.replace(/\D/g, '');
        if (cleaned.length === 11) return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        if (cleaned.length === 14) return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        return documento;
    };

    const getStatusLabel = (ativo: string | undefined) => {
        switch (ativo) {
            case 'A': return 'Ativo';
            case 'I': return 'Inativo';
            case 'D': return 'Desativado';
            case 'P': return 'Prospect';
            default: return 'Inativo';
        }
    };

    const getStatusClass = (ativo: string | undefined) => {
        switch (ativo) {
            case 'A': return styles.statusActive;
            case 'I':
            case 'D': return styles.statusInactive;
            case 'P': return styles.statusProspect;
            default: return styles.statusInactive;
        }
    };

    const getPaginationButtons = () => {
        const pageNumbers: (number | string)[] = [];
        const maxButtons = 5;
        const halfMax = Math.floor(maxButtons / 2);

        if (totalPages <= maxButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            if (currentPage > halfMax + 2) pageNumbers.push('...');

            const start = Math.max(2, currentPage - halfMax);
            const end = Math.min(totalPages - 1, currentPage + halfMax);

            for (let i = start; i <= end; i++) pageNumbers.push(i);

            if (currentPage < totalPages - halfMax - 1) pageNumbers.push('...');
            if (totalPages > 1) pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const activeCount = filterCounts.status.find(s => s.ATIVO === 'A')?.count || 0;
    const inactiveCount = filterCounts.status.find(s => s.ATIVO === 'I')?.count || 0;

    return (
        <div className={styles.pageContainer}>
            {/* ── Filter Bar (Single Row for Clients because there are no date filters) ── */}
            <div className={styles.filterBar}>
                <div className={styles.kpiGroup}>
                    <span className={styles.kpiMicroItem}>
                        <PeopleAltIcon style={{ fontSize: 16, color: '#64748b' }} />
                        <span className={styles.kpiMicroLabel}>Total:</span>
                        <span className={styles.kpiMicroValue}>{totalItems.toLocaleString('pt-BR')}</span>
                    </span>
                    <span className={styles.kpiMicroItem}>
                        <CheckCircleIcon style={{ fontSize: 16, color: '#16a34a' }} />
                        <span className={styles.kpiMicroLabel}>Ativos:</span>
                        <span className={styles.kpiMicroValue}>{activeCount.toLocaleString('pt-BR')}</span>
                    </span>
                    <span className={styles.kpiMicroItem}>
                        <HighlightOffIcon style={{ fontSize: 16, color: '#ef4444' }} />
                        <span className={styles.kpiMicroLabel}>Inativos:</span>
                        <span className={styles.kpiMicroValue}>{inactiveCount.toLocaleString('pt-BR')}</span>
                    </span>
                </div>
            </div>

            {/* ── Table ── */}
            <div className={styles.tableCard}>
                <div className={styles.tableContainer}>
                    <div className={styles.tableToolbar}>
                        <div className={styles.tableSearchBar}>
                            <SearchIcon className={styles.tableSearchIcon} />
                            <input
                                type="text"
                                placeholder="Buscar por nome, fantasia ou CPF/CNPJ..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className={styles.tableSearchInput}
                            />
                            {searchTerm && (
                                <ClearIcon
                                    className={styles.tableSearchClear}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setCurrentPage(1);
                                    }}
                                />
                            )}
                        </div>

                        <div className={styles.tableFilters}>
                            {isSupervisor && <CarteiraSelector />}
                            <CustomSelect
                                id="ordenacao-select"
                                value={selectedOrdenacao}
                                onChange={(e) => { setSelectedOrdenacao(e.target.value); setCurrentPage(1); }}
                                options={ORDENACAO_OPTIONS}
                                label="Ordenação"
                            />
                            <CustomSelect
                                id="segmento-select"
                                value={selectedSegmento}
                                onChange={(e) => { setSelectedSegmento(e.target.value); setCurrentPage(1); }}
                                placeholder="Segmento"
                                label="Segmento"
                                options={[{ value: '', label: 'Todos' }, ...filterCounts.segmentos.map((seg) => ({ value: String(seg.ID), label: seg.DESCRICAO || '', count: seg.count }))]}
                            />
                            <CustomSelect
                                id="estado-select"
                                value={selectedEstado}
                                onChange={(e) => { setSelectedEstado(e.target.value); setCurrentPage(1); }}
                                placeholder="Estado"
                                label="Estado"
                                options={[{ value: '', label: 'Todos' }, ...filterCounts.estados.map((est) => ({ value: est.ESTADO || '', label: est.ESTADO || '', count: est.count }))]}
                            />
                            <CustomSelect
                                id="status-select"
                                value={selectedAtivo}
                                onChange={(e) => { setSelectedAtivo(e.target.value); setCurrentPage(1); }}
                                placeholder="Status"
                                label="Status"
                                options={[{ value: '', label: 'Todos' }, ...filterCounts.status.filter((s) => s.ATIVO && s.ATIVO.trim() !== '').map((s) => ({ value: s.ATIVO!, label: getStatusLabel(s.ATIVO), count: s.count }))]}
                            />
                            {hasActiveFilters && (
                                <button className={styles.clearBtn} onClick={clearFilters}>
                                    <ClearIcon style={{ fontSize: 16 }} /> Limpar
                                </button>
                            )}
                            {!isSupervisor && !isAws && (
                                <button className={styles.newClientBtn} onClick={handleNewClient}>
                                    <AddIcon fontSize="small" /> Novo Cliente
                                </button>
                            )}
                        </div>
                    </div>
                    {loading ? (
                        <>
                            <table className={styles.clientsTable}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nome / Razão Social</th>
                                        <th>Nome Fantasia</th>
                                        <th>CPF/CNPJ</th>
                                        <th>Cidade</th>
                                        <th>Estado</th>
                                        <th>Telefone</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                            </table>
                            <ClientesPageSkeleton />
                        </>
                    ) : error ? (
                        <p className={styles.errorMessage}>{error}</p>
                    ) : (
                        <>
                            <table className={styles.clientsTable}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nome / Razão Social</th>
                                        <th>Nome Fantasia</th>
                                        <th>CPF/CNPJ</th>
                                        <th>Cidade</th>
                                        <th>Estado</th>
                                        <th>Telefone</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {clients.length > 0 ? (
                                        clients.map((client) => (
                                            <tr key={client.ID} onClick={() => handleClientClick(client.ID)}>
                                                <td><span className={styles.cellId}>{client.ID}</span></td>
                                                <td><span className={styles.cellName}>{client.NOME}</span></td>
                                                <td><span className={styles.cellFantasy}>{client.FANTASIA || '-'}</span></td>
                                                <td><span className={styles.cellDocument}>{formatCPFCNPJ(client.DOCUMENTO)}</span></td>
                                                <td>{client.CIDADE || '-'}</td>
                                                <td>{client.ESTADO || '-'}</td>
                                                <td>{client.DDD && client.TELEFONE ? `(${client.DDD}) ${client.TELEFONE}` : client.TELEFONE || '-'}</td>
                                                <td>{client.EMAIL || '-'}</td>
                                                <td>
                                                    <span className={`${styles.statusBadge} ${getStatusClass(client.ATIVO)}`}>
                                                        {getStatusLabel(client.ATIVO)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={9} className={styles.noResults}>
                                                Nenhum cliente encontrado
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>

                {!loading && !error && totalPages > 1 && (
                    <div className={styles.pagination}>
                        <div className={styles.pageButtons}>
                            <button
                                className={styles.pageButton}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                <ChevronLeftIcon fontSize="small" />
                                Anterior
                            </button>

                            <div className={styles.pageNumbers}>
                                {getPaginationButtons().map((item, index) =>
                                    item === '...' ? (
                                        <span key={index} className={styles.pageEllipsis}>...</span>
                                    ) : (
                                        <button
                                            key={index}
                                            onClick={() => { setCurrentPage(item as number); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                            className={`${styles.pageNumber} ${item === currentPage ? styles.pageNumberActive : ''}`}
                                        >
                                            {item}
                                        </button>
                                    ),
                                )}
                            </div>

                            <button
                                className={styles.pageButton}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                Próxima
                                <ChevronRightIcon fontSize="small" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <ClientDetailModal
                    clientId={selectedClientId}
                    onClose={handleModalClose}
                    onSuccess={handleModalSuccess}
                />
            )}
        </div>
    );
};

export default ClientesPage;
